/**
 * Products against webback (CU-869fhuhy6): `GET|POST /setup/products`,
 * `GET|PUT|DELETE /setup/products/<id>`, `DELETE …/rows/<row_id>`.
 *
 * Not `useSetupCollection`: that hook saves the whole collection on every
 * change, 600 ms after the edit, and toasts the refusal. A product is
 * saved with a button and the refusal is painted inside the card, on the
 * guilty row, with the server's sentence (UX v3 D13/D15, the one Setup
 * tab that deviates from save-on-blur, by decision) — so each mutation is
 * one explicit request whose `ApiError` goes back to the card.
 *
 *   const { items, loading, error, reload, save, remove, removeRow } = useProducts()
 *
 * `save(product)` returns the saved product (client shape); `remove(id)`
 * and `removeRow(productId, rowId)` throw the `ApiError` the card turns
 * into the dialog with no red button (`product_in_use`, `row_in_use`).
 *
 * `ProductsProvider` / `useProductsCatalog` hand the same list, read-only,
 * to the screens that only need to look at it — the wizard's product
 * picker, the Strategies table, Executions — loaded once per page.
 */
import { isClientId } from 'Pages/DsRepricing/Setup/@services/adapters'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { setupApi } from './api'
import { products as adapter } from './adapters'

export function useProducts({ auto = true } = {}) {
	const [state, setState] = useState({ items: [], loading: auto, error: null })

	const reload = useCallback(async ({ quiet = false } = {}) => {
		if (!quiet) setState(s => ({ ...s, loading: true }))
		try {
			const rows = await setupApi.products.list()
			setState({ items: rows.map(adapter.fromServer), loading: false, error: null })
		} catch (e) {
			setState(s => ({ items: s.items, loading: false, error: e }))
		}
	}, [])

	useEffect(() => { if (auto) reload() }, [auto, reload])

	const save = useCallback(async (product) => {
		const dto = adapter.toServer(product)
		// A card hands its draft over without `_new` (`{ _new, ...clean }`), so the
		// id decides: a client id (`new_…`) has never been saved → POST, never PUT
		// (Curro, 2026-10-20: "Not saved. product not found" on the first product).
		const isNew = product.id == null || product._new || isClientId(product.id)
		const saved = adapter.fromServer(isNew
			? await setupApi.products.create(dto)
			: await setupApi.products.update(product.id, dto))
		setState(s => ({ ...s, items: isNew
			? [saved, ...s.items]
			: s.items.map(p => (String(p.id) === String(saved.id) ? saved : p)) }))
		return saved
	}, [])

	const remove = useCallback(async (id) => {
		await setupApi.products.remove(id)
		setState(s => ({ ...s, items: s.items.filter(p => String(p.id) !== String(id)) }))
	}, [])

	const removeRow = useCallback(async (productId, rowId) => {
		await setupApi.deleteProductRow(productId, rowId)
		setState(s => ({ ...s, items: s.items.map(p => (String(p.id) !== String(productId) ? p
			: { ...p, rows: (p.rows || []).filter(r => String(r.id) !== String(rowId)) })) }))
	}, [])

	return { ...state, reload, save, remove, removeRow }
}

const ProductsCatalog = createContext({ items: [], loading: true, error: null, reload: () => {} })

/** The tenant's products, read-only, once per page. */
export function ProductsProvider({ children }) {
	const { items, loading, error, reload } = useProducts()
	const value = useMemo(() => ({ items, loading, error, reload }), [items, loading, error, reload])
	return <ProductsCatalog.Provider value={value}>{children}</ProductsCatalog.Provider>
}

export function useProductsCatalog() {
	return useContext(ProductsCatalog)
}

/** The product with this id from the catalogue, or null. */
export function useProductById(id) {
	const { items } = useProductsCatalog()
	return useMemo(() => (id == null ? null : items.find(p => String(p.id) === String(id)) || null), [items, id])
}
